/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { DOCUMENT } from "@angular/common";
import {
	AfterViewInit,
	Directive,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Renderer2
} from "@angular/core";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { Subject, takeUntil } from "rxjs";
import { SelectOption } from "../models/ui/select-option.model";

@Directive({
	selector: "[globalictccSearchInSelect]",
	standalone: true
})
export class SearchInSelectDirective<T> implements OnInit, AfterViewInit, OnDestroy {
	// This should be refactored when we'll merge Angular v20 PR!
	@Input("globalictccSearchInSelect") public enabled: boolean = true;

	/**
	 * When true, disables local filtering and emits raw search input via `paginatedSearchChange`
	 * instead. The parent is responsible for fetching filtered options from the API.
	 */
	@Input() public paginatedMode: boolean = false;

	// When init, initialize `originalOptions`
	@Input() public set options(value: SelectOption[]) {
		// In paginated mode keep _originalOptions in sync so closing restores the latest set of options
		if (this.paginatedMode || !this._originalOptions.length) {
			this._originalOptions = value;
		}
	}

	@Output() public optionsChange = new EventEmitter<SelectOption[]>();

	/**
	 * Emitted (without debounce) on every input change when `paginatedMode` is true.
	 * The consumer is responsible for debouncing and calling the paginated API.
	 */
	@Output() public paginatedSearchChange = new EventEmitter<string>();

	@Input() public filterFn = (option: MatOption<T>, inputValue: string) =>
		!option.viewValue?.toLowerCase().includes(inputValue?.toLowerCase() ?? "");

	private _destroy$ = new Subject<void>();
	private _input: HTMLInputElement | null = null;
	private _selectValueContainer: HTMLElement | null = null;
	private _originalOptions: SelectOption[] = [];

	/**
	 * Create the input element with styling and event listeners
	 */
	private _createInputElement(): HTMLInputElement {
		const input = this._document.createElement("input");
		input.classList.add("select-search-input");
		input.setAttribute("type", "text");
		input.setAttribute("autocomplete", "off");
		input.setAttribute("placeholder", "Search...");

		// Add input event listener for filtering
		this._renderer.listen(input, "input", () => this._onInputChange());

		// Prevent click from closing the dropdown
		this._renderer.listen(input, "click", event => {
			event.stopPropagation(); // Prevent closing the dropdown
		});

		// Handle keyboard navigation
		this._renderer.listen(input, "keydown", event => {
			this._handleKeyDown(event);
		});

		// Place cursor at the end of input
		input.setSelectionRange(0, 1000);

		return input;
	}

	private get _inputElement(): HTMLInputElement {
		if (!this._input) {
			this._input = this._createInputElement();
		}

		return this._input as HTMLInputElement;
	}

	constructor(
		private _select: MatSelect,
		private _renderer: Renderer2,
		@Inject(DOCUMENT) private _document: Document
	) {}

	ngOnInit(): void {
		if (!this.enabled) return;
	}

	/**
	 * Handle input changes to filter the dropdown options
	 * Always filter from _originalOptions, emit filtered list
	 */
	private _onInputChange() {
		if (!this.enabled) return;

		const inputValue = this._inputElement.value.trim();

		if (this.paginatedMode) {
			// In paginated mode: skip local filtering, let the parent handle it via API
			this.paginatedSearchChange.emit(inputValue);
			return;
		}

		let filtered: SelectOption[];

		if (!inputValue || inputValue === "") {
			// Restore original options if input is empty
			filtered = [...this._originalOptions];
		} else {
			// Filter from original options
			filtered = this._originalOptions.filter(opt =>
				opt.label?.toLowerCase().includes(inputValue.toLowerCase())
			);
		}

		this.optionsChange.emit(filtered);
	}

	/**
	 * Handle keyboard events for the input field
	 */
	private _handleKeyDown(event: KeyboardEvent) {
		if (!this.enabled) return;

		// Space key should not close the dropdown (default behavior)
		if (event.key === " ") {
			event.stopPropagation();
			return;
		}
	}

	/**
	 * Implements the ngAfterViewInit lifecycle hook
	 * This is where we set up the DOM manipulation to insert our input element
	 */
	ngAfterViewInit(): void {
		if (!this.enabled) return;

		const selectElement = this._select._elementRef.nativeElement;
		this._selectValueContainer = selectElement.querySelector(".mat-mdc-select-value");

		if (!this._selectValueContainer) {
			console.error("Could not find .mat-mdc-select-value element");
			return;
		}

		// Set up the openedChange subscription to replace content when dropdown opens
		this._setupOpenCloseHandler();
	}

	/**
	 * Sets up the handler for opening and closing the select dropdown
	 * This is where we replace the select value with our input and restore it when closing
	 */
	private _setupOpenCloseHandler(): void {
		this._select.openedChange.pipe(takeUntil(this._destroy$)).subscribe(isOpen => {
			if (isOpen) {
				// Insert our input (CSS :has(.select-search-input) hides the value text automatically)
				this._renderer.appendChild(this._selectValueContainer, this._inputElement);

				// Focus the input
				this._inputElement.focus();

				// If there's a selected option, set its text in the input
				this._updateInputFromSelection();
			} else {
				// When closing, reset options filtering
				this._resetOptionsFiltering();

				// In paginated mode the parent manages options; do not override them on close
				if (!this.paginatedMode) {
					this.optionsChange.emit(this._originalOptions);
				}
			}
		});
	}

	/**
	 * Updates the input value based on the currently selected option
	 */
	private _updateInputFromSelection(): void {
		if (!this._input) return;

		const selectedOption = this._select.selected;

		if (selectedOption) {
			if (Array.isArray(selectedOption) && selectedOption.length === 1) {
				this._input.value = selectedOption[0].viewValue;
			} else if (!Array.isArray(selectedOption) && selectedOption.viewValue) {
				this._input.value = selectedOption.viewValue;
			}
		} else {
			this._input.value = "";
		}
	}

	/**
	 * Resets the filtering on all options when closing the dropdown
	 */
	/**
	 * Resets the filtering on all options when closing the dropdown
	 * Restores original options and emits them
	 */
	private _resetOptionsFiltering(): void {
		this.optionsChange.emit(this._originalOptions);
		if (this._input) {
			this._input.value = ""; // clear so the next open always starts with an empty search box
			this._input.remove();
		}
	}

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}
}
